import { useMemo } from "react";
import { Button, Flex, Heading, Modal, Paragraph } from "@ir/ui";

interface MasterDataJsonPreviewModalProps {
  isOpen: boolean;
  title: string;
  value: unknown;
  onClose: () => void;
}

function formatJsonValue(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return "";
    try {
      return JSON.stringify(JSON.parse(trimmed), null, 2);
    } catch {
      return value;
    }
  }
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return "[Unable to display value]";
  }
}

/** Read-only modal for Master Data JSON/object fields. */
export const MasterDataJsonPreviewModal = ({
  isOpen,
  title,
  value,
  onClose,
}: MasterDataJsonPreviewModalProps) => {
  const formatted = useMemo(() => formatJsonValue(value), [value]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      classNames={{
        body: "master-data-json-preview-modal__body",
      }}
      headerContent={
        <Heading level={6} className="text-grey-700 font-bold m-0">
          {title}
        </Heading>
      }
      footerContent={
        <Flex justifyContent="end">
          <Button size="small" color="grey" onPress={onClose}>
            Close
          </Button>
        </Flex>
      }
    >
      {formatted ? (
        <pre className="master-data-json-preview-modal__content">{formatted}</pre>
      ) : (
        <Paragraph className="text-grey-500 m-0">No content</Paragraph>
      )}
    </Modal>
  );
};

/** Purchaser fields that store JSON and are shown via eye icon in the table. */
export const MASTER_DATA_PURCHASER_JSON_FIELDS = new Set([
  "metadata",
  "intelligenceFilter",
  "deductionTypeInvoicePatternForDiscount",
  "additionalFieldMapping",
]);
